import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, Minus, CheckCircle2, ShoppingCart, Loader2, UserCheck } from "lucide-react";
import { useOrderRequest } from "@/contexts/OrderRequestContext";
import { toast } from "@/hooks/use-toast";
import { validateEmail } from "@/lib/validateEmail";

interface Props {
  /** Called after a successful submission (e.g. to close a modal). */
  onSubmitted?: () => void;
}

/**
 * Renders the site-wide order request: the selected items (with quantity controls)
 * plus the required contact form. Used by both the floating cart modal and the
 * dedicated /request-quote/ page so the two stay in sync.
 *
 * Entering an email that matches a previous requester auto-fills the company,
 * contact name and phone (all editable) via the lookup-company-profile function.
 */
export default function RequestQuoteForm({ onSubmitted }: Props) {
  const { items, totalQty, setQty, remove, clear, submit, lookupProfile } = useOrderRequest();
  const [company, setCompany] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [emailError, setEmailError] = useState("");
  const [lookingUp, setLookingUp] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const lookedUpEmail = useRef<string>("");

  const checkEmail = (value: string) => {
    if (!value.trim()) {
      setEmailError("");
      return false;
    }
    const c = validateEmail(value);
    setEmailError(c.valid ? "" : c.message);
    return c.valid;
  };

  // On a valid email, look up a saved profile and pre-fill (once per email).
  const handleEmailBlur = async (value: string) => {
    const clean = value.trim().toLowerCase();
    if (!clean || !checkEmail(clean)) return;
    if (clean === lookedUpEmail.current) return;
    lookedUpEmail.current = clean;
    setLookingUp(true);
    const profile = await lookupProfile(clean);
    setLookingUp(false);
    if (profile) {
      if (profile.company_name) setCompany(profile.company_name);
      if (profile.contact_name) setName(profile.contact_name);
      if (profile.phone) setPhone(profile.phone);
      setAutofilled(true);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!company.trim()) {
      toast({ title: "Company name required", description: "Please enter your company name.", variant: "destructive" });
      return;
    }
    if (!name.trim()) {
      toast({ title: "Contact name required", description: "Please enter your contact name.", variant: "destructive" });
      return;
    }
    if (!phone.trim()) {
      toast({ title: "Phone required", description: "Please enter your phone number.", variant: "destructive" });
      return;
    }
    if (!email.trim() || !checkEmail(email)) {
      toast({ title: "Email required", description: "Please enter a valid email address.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const res = await submit({ company, name, phone, email, message });
    setSubmitting(false);
    if (!res.ok) {
      toast({ title: "Could not submit request", description: res.error, variant: "destructive" });
      return;
    }
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="py-8 text-center space-y-4">
        <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto" />
        <h3 className="text-xl font-semibold">Thank you for your request</h3>
        <p className="mx-auto max-w-md text-sm text-muted-foreground leading-relaxed">
          Please allow us 2-3 business days to review your request and get in contact. In the meantime, feel free to
          continue browsing our products and services and add any additional items to your order request.
        </p>
        <Button asChild variant="outline" className="mt-2" onClick={() => onSubmitted?.()}>
          <Link to="/cleaning-equipment/">Continue browsing</Link>
        </Button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="py-10 text-center text-muted-foreground">
        <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-40" />
        <p>
          Your request list is empty. Browse the catalog and use <strong>Add to request</strong> on any product.
        </p>
        <div className="mx-auto mt-6 max-w-md rounded-xl border border-border bg-muted/40 p-5 text-left">
          <p className="text-sm font-semibold text-foreground">Popular starting points</p>
          <ul className="mt-3 space-y-2 text-sm">
            <li>
              <Link to="/cleaning-equipment/under-carriage-sprayers/the-neutralizer/" className="font-medium text-primary hover:underline">
                The Neutralizer™
              </Link>
              <span className="text-muted-foreground"> — undercarriage salt neutralizing system</span>
            </li>
            <li>
              <Link to="/touchless-drive-thru/" className="font-medium text-primary hover:underline">
                Touchless Truck Wash Systems
              </Link>
              <span className="text-muted-foreground"> — automatic drive-through washing</span>
            </li>
            <li>
              <Link to="/cleaning-equipment/pressure-washers/hotsy-pressure-washers/" className="font-medium text-primary hover:underline">
                Hotsy Pressure Washers
              </Link>
              <span className="text-muted-foreground"> — hot &amp; cold water models</span>
            </li>
          </ul>
        </div>
        <p className="mt-5 text-sm">
          Not sure what you need?{" "}
          <Link to="/services/free-consultations/" className="font-semibold text-primary hover:underline">
            Get a free consultation
          </Link>{" "}
          or call us at{" "}
          <a href="tel:4195020007" className="font-semibold text-primary hover:underline">
            419-502-0007
          </a>
        </p>
        <Button asChild variant="outline" className="mt-4" onClick={() => onSubmitted?.()}>
          <Link to="/cleaning-equipment/">Browse products</Link>
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-5"
      toolname="submit_order_request"
      tooldescription="Submit an order request quote to Enzo's Cleaning Solutions for the products already added to the request list, with the requester's contact details."
    >
      <div>
        <h3 className="text-sm font-semibold mb-2">Selected products ({totalQty})</h3>
        <div className="space-y-2">
          {items.map((item) => {
            const k = `${item.product_sku || ""}::${item.product_name}`;
            return (
              <div key={k} className="flex items-center gap-3 border border-border rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{item.product_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {item.product_sku ? <span className="font-mono">{item.product_sku}</span> : null}
                    {item.category ? <span> · {item.category}</span> : null}
                  </div>
                </div>
                <div className="inline-flex items-center gap-1 rounded-lg border border-border px-1">
                  <button type="button" onClick={() => setQty(item.product_sku, item.product_name, item.quantity - 1)} className="p-1.5 hover:bg-muted rounded" aria-label="Decrease quantity">
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="min-w-[2ch] text-center text-sm font-semibold tabular-nums">{item.quantity}</span>
                  <button type="button" onClick={() => setQty(item.product_sku, item.product_name, item.quantity + 1)} className="p-1.5 hover:bg-muted rounded" aria-label="Increase quantity">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <button type="button" onClick={() => remove(item.product_sku, item.product_name)} className="p-2 text-muted-foreground hover:text-destructive" aria-label="Remove item">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
        <button type="button" onClick={clear} className="mt-2 text-xs text-muted-foreground hover:text-destructive">
          Clear all items
        </button>
      </div>

      <div className="border-t border-border pt-4">
        <h3 className="text-sm font-semibold mb-3">Your contact information</h3>

        {autofilled && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary">
            <UserCheck className="h-4 w-4 flex-shrink-0" />
            Welcome back — we filled in your saved details. Feel free to edit anything before submitting.
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-1">
            <label className="block text-sm font-medium mb-1">
              Company name <span className="text-destructive">*</span>
            </label>
            <Input name="company" toolparamdescription="The requester's company or organization name" value={company} onChange={(e) => setCompany(e.target.value)} required />
          </div>
          <div className="sm:col-span-1">
            <label className="block text-sm font-medium mb-1">
              Contact name <span className="text-destructive">*</span>
            </label>
            <Input name="name" toolparamdescription="The requester's contact name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="sm:col-span-1">
            <label className="block text-sm font-medium mb-1">
              Email <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <Input
                type="email"
                name="email"
                toolparamdescription="The requester's email address"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError("");
                }}
                onBlur={(e) => handleEmailBlur(e.target.value)}
                aria-invalid={!!emailError}
                className={emailError ? "border-destructive pr-9" : "pr-9"}
                required
              />
              {lookingUp && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            {emailError && <p className="mt-1 text-xs text-destructive">{emailError}</p>}
          </div>
          <div className="sm:col-span-1">
            <label className="block text-sm font-medium mb-1">
              Phone <span className="text-destructive">*</span>
            </label>
            <Input type="tel" name="phone" toolparamdescription="A phone number where the requester can be reached" value={phone} onChange={(e) => setPhone(e.target.value)} required />
          </div>
        </div>
        <div className="mt-3">
          <label className="block text-sm font-medium mb-1">Notes / specific questions (optional)</label>
          <Textarea rows={3} name="message" toolparamdescription="Optional project details, delivery timing, or specific questions" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Project details, delivery timing, specific questions, etc." />
        </div>
      </div>

      <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
        {submitting ? "Submitting…" : "Submit Order Request"}
      </Button>
    </form>
  );
}
